import { type ReactElement, useCallback, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import CloseIcon from "@mui/icons-material/Close"
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, useMediaQuery, useTheme } from "@mui/material"
import { Button, NotificationUtils, Select, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError } from "axios"

import { Dropzone } from "../../components/form/Dropzone"
import { getTheme } from "../../hooks/UseTheme"
import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import { type StandardErrorType } from "../../utils/ErrorHandling"

interface DataFileUploadProps {
  open: boolean
  onClose: () => void
  type?: DataFileGroup.type
}

export function DataFileUpload(props: Readonly<DataFileUploadProps>): ReactElement {
  const { open, onClose, type } = props
  const { id: projectID } = useParams<{ id: string }>()

  const [searchParams] = useSearchParams()
  const fileId = searchParams.get("file_id")

  const MuiTheme = useTheme()

  const theme = getTheme()

  const { t } = useTranslation("datafiles")

  const [uploadProgress, setUploadProgress] = useState<number>(0)
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
  const [fileType, setFileType] = useState<DataFileGroup.type>(DataFileGroup.type.GENERIC)

  const abortControllerRef = useRef<AbortController | null>(null)

  /**
   * Handles file upload using react-query mutation
   * Processes the file and updates the columns state based on file type (CSV/JSON)
   * @param {File} file - The file to be uploaded
   * @returns {Promise<DataFileGroup>} The uploaded file data
   */
  const {
    isLoading: isUploadingFile,
    mutateAsync: uploadFile,
    reset,
  } = useMutation<DataFileGroup, AxiosError<StandardErrorType>, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.uploadDataFile({
        project_uuid: projectID as string,
        file: file,
        setProgress: setUploadProgress,
        signal: abortControllerRef.current.signal,
        type: fileType,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: async () => {
        await queryClient.invalidateQueries(["data-files", projectID])

        NotificationUtils.toast(t("uploadDialog.successUpdate"), {
          severity: "success",
        })
      },
    },
  )

  /**
   * Handles file upload using react-query mutation
   * Processes the file and updates the columns state based on file type (CSV/JSON)
   * @param {File} file - The file to be uploaded
   * @returns {Promise<DataFileGroup>} The uploaded file data
   */
  const {
    isLoading: isUpdatingFile,
    mutateAsync: updateFile,
    reset: resetUpdateMutation,
  } = useMutation<DataFileGroup, AxiosError, File>(
    (file: File) => {
      abortControllerRef.current = new AbortController()
      return KonanAPI.updateDataFile({
        project_uuid: projectID as string,
        data_file_uuid: fileId as string,
        file: file,
        setProgress: setUploadProgress,
        signal: abortControllerRef.current.signal,
      })
    },
    {
      mutationKey: "uploadCSV",
      onSuccess: async () => {
        await queryClient.invalidateQueries(["file-retrieve", projectID, fileId])

        NotificationUtils.toast(t("uploadDialog.successUpdate"), {
          severity: "success",
        })
      },
    },
  )

  /**
   * Aborts ongoing upload and resets component state
   */
  const handleCloseAndAbort = useCallback(() => {
    abortControllerRef.current?.abort()
    isUpdatingFile ? resetUpdateMutation() : reset()
    setUploadProgress(0)
    setAcceptedFiles([])
  }, [isUpdatingFile, reset, resetUpdateMutation])

  /**
   * Handles the file upload process
   * For JSON files, extracts column names from the first 5 records
   * For CSV files, uses the columns provided by the API
   */
  const handleUpload = async (): Promise<void> => {
    try {
      if (!acceptedFiles[0]) return

      if (type) await updateFile(acceptedFiles[0])
      else await uploadFile(acceptedFiles[0])

      onClose()
    } catch {
      // reset dropzone on error
      handleCloseAndAbort()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("sm"))}
    >
      <DialogTitle className="dialog-header-base" style={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h2-bold">{t("uploadDialog.title")}</Typography>

        <IconButton onClick={onClose} size="small" className="close-icon-button">
          <CloseIcon style={{ color: theme.palette.base.text[2] }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className="dialog-base">
        <Grid container gap={3}>
          <Grid item xs={12}>
            <Dropzone
              uploadingCSV={isUploadingFile}
              uploadProgress={uploadProgress}
              resetProgress={() => setUploadProgress(0)}
              selectedFiles={acceptedFiles}
              bottomText={t("uploadDialog.maxSize")}
              maxSize={2e9}
              extensions={{ "application/json": [".json"], "text/*": [".csv"] }}
              description={t("uploadDialog.dropzoneTitle")}
              setSelectedFiles={function (file: File[]): void {
                setAcceptedFiles(file)
              }}
              handleCancelUpload={handleCloseAndAbort}
            />
          </Grid>

          {!type && !(isUpdatingFile || isUploadingFile) && (
            <Grid item xs={12}>
              <Select
                label={t("uploadDialog.fileType")}
                optionsWithValues={[
                  { label: t("datafiletypes.GENERIC"), value: DataFileGroup.type.GENERIC },
                  { label: t("datafiletypes.CONDITION_LIST"), value: DataFileGroup.type.CONDITION_LIST },
                  { label: t("datafiletypes.PREDICTIONS"), value: DataFileGroup.type.PREDICTIONS },
                  { label: t("datafiletypes.ACTUAL_DECISIONS"), value: DataFileGroup.type.ACTUAL_DECISIONS },
                ]}
                value={fileType}
                handleChange={(e) => setFileType(e.target.value as DataFileGroup.type)}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              fullWidth
              variant={isUploadingFile || isUpdatingFile ? "dangerous" : "primary"}
              onClick={isUploadingFile || isUpdatingFile ? handleCloseAndAbort : handleUpload}
              disabled={acceptedFiles.length === 0}
            >
              {isUploadingFile || isUpdatingFile ? t("uploadDialog.abort") : t("uploadDialog.uploadFile")}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
